/*
 * WM Delete Item Tasks (legacy WMD0124).
 *
 * The page's own bands, captions and coded-field vocabularies — reproductions of one Delphi form rather
 * than messages the app shares. Notify/confirm text lives in common/Messages.js, as on every sibling.
 */

// Parameter field names, shared by Layout.js (the schema) and the page (formRefs lookups and the request
// body). Declared once so a rename cannot silently break the page's imperative field access, which is
// keyed by these exact strings. They are also the request property names the API binds, so they match
// DeleteItemTasksDTO.
export const FIELD = Object.freeze({
    StartDate: "StartDate",
    NumberOfDays: "NumberOfDays",
});

/**
 * WMITEMTASKDETAILS.STATUS. Codes and captions verbatim from uWMM0124.dfm's Codes.Strings /
 * Items.Strings pair, in its order — except the spelling.
 *
 * The DFM writes 'Z' as "Commited", with one 't'. Fixed here: a plain typo is not a convention worth
 * reproducing, and every other WM screen in the web app spells it correctly.
 *
 * Mirrors Argos.Core/Constants/DeleteItemTasksConstants.cs -> ItemTaskStatus. This is a TASK status, not
 * a transaction status — the two share only the letter 'Z'.
 */
export const TASK_STATUS_OPTIONS = [
    { Value: "A", Text: "Available" },
    { Value: "C", Text: "Completed" },
    { Value: "I", Text: "In Progress" },
    { Value: "N", Text: "Not Available" },
    { Value: "Z", Text: "Committed" },
];

/**
 * WMITEMTASKDETAILS.TRANSACTIONTYPE. Codes and captions verbatim from uWMM0124.dfm, in its order.
 *
 * NOT WMTRANSACTIONHEADER's transaction types, which share letters and mean different things by them:
 * 'S' is Shipment in both but 'N' is Non-Inventory Item there and Location Not Available here, and 'T'
 * and 'P' do not exist there at all.
 *
 * 'T' (Location Transfer) is the only type the first arm of the server's WHERE accepts — a task of any
 * other type reaches the grid through its committed transaction header instead.
 */
export const TASK_TRANSACTION_TYPE_OPTIONS = [
    { Value: "M", Text: "Miscellaneous" },
    { Value: "N", Text: "Location Not Available" },
    { Value: "P", Text: "Replenishment" },
    { Value: "R", Text: "Receipt (Putaway)" },
    { Value: "S", Text: "Shipment (Pick)" },
    { Value: "T", Text: "Location Transfer" },
];

/**
 * The Start Date as "yyyy-MM-dd", or null when the box is empty.
 *
 * NOT the Date object straight off the DateBox, and not toISOString(). The field is a calendar date that
 * the server measures a cut-off back from, and a DateBox value is local midnight — so east of UTC,
 * toISOString() moves it to the previous evening and silently shifts the cut-off a day, which on this
 * screen is a day's worth of tasks deleted or spared. A bare date string binds to DateTime as that day
 * at 00:00 with no zone arithmetic at all, which is also what the server's dbo.fnab_removetime expects
 * to be handed.
 *
 * Built from the LOCAL getFullYear/getMonth/getDate for the same reason: the user picked the date they
 * see.
 */
export const toDateOnly = (value) => {
    if (!value) return null;

    const parsed = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;

    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const day = String(parsed.getDate()).padStart(2, "0");

    return `${parsed.getFullYear()}-${month}-${day}`;
};

/** Saved-layout identity for the task grid. One key — there is only ever one column set. */
export const GRID_KEY = "7C2E95B1-4A68-43D7-85F0-29C6B18E4A3D";

// GridLayoutManager's built-in layout name, used as the initial currentLayout state.
export const DEFAULT_LAYOUT_NAME = "Default";

// Dynamic-form layout identity for the parameters panel.
export const LAYOUT_ID = "5E83A6C2-91B7-4D40-A6F1-38C0792D5B64";
export const LAYOUT_NAME = "WMDeleteItemTasksForm";

// The form's lblTitle (uWMD0124.dfm), verbatim. The window caption adds "(WMD0124)"; the title band is
// the one users read. The menu leaf is the shorter "Delete Item Tasks", as AI8's menu has it.
export const PAGE_TITLE = "Delete WM Item Tasks";

/*
 * The read-only criteria memo (uWMD0124.dfm edtMemo), with the Delphi's leading indentation collapsed.
 *
 * Two words are corrected: "less then" -> "less than", and "Transactions that has associated tasks" ->
 * "that have". Both are plain mistakes rather than ABECAS terminology, and this band is the only place
 * the screen explains what it deletes, so it is worth it reading correctly. Nothing else is paraphrased.
 */
export const HELP_TEXT =
    "Warning: This Program will DELETE Item Tasks.\n"
    + "(1) Tasks must be in committed status or Transactions that have associated tasks must be "
    + "committed.\n"
    + "(2) Tasks Completed Date must be less than selected date in question form.";

// Group-box caption for the question panel. The Delphi's grpBottomLeft carries no caption of its own -
// just the two field labels - so this one is ours, named for what the panel is.
export const PARAMETERS_TITLE = "Questions";

// Field captions, verbatim from the DFM.
export const FIELD_LABEL = Object.freeze({
    StartDate: "Start Date",
    NumberOfDays: "Number Days",
});

// Toolbar button captions, verbatim from the Delphi form (uWMD0124.dfm grpBottomButtons), in its
// left-to-right order. Help and Exit are not migrated: Help only ever showed "Help on selected topic is
// not available at this time." (MSG_STR_HELP), and Exit closed the MDI child.
export const BUTTON = Object.freeze({
    Start: "Start",
    Preview: "Preview",
    Reset: "Reset",
});

// Matches the Delphi DBArgosNumberCombo display for a whole-number spin edit.
export const INTEGER_FORMAT = "#,##0";

// The Delphi allows the procedure 300s (uWMD0124.pas Process, added in the 10/06/2008 revision because
// the default was not enough) and Start runs a count either side of it.
export const REQUEST_TIMEOUT_MS = 10 * 60 * 1000;
